import React from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { CrmEvent, CrmIntakeView } from '../../../features/crm/crmApi'
import { courseTitle, dictLabel, fmtDate, money, subjectTitle, whenShort } from './format'

const NOTIFY_STATUSES = ['SENT', 'NO_LINK', 'NO_BOT', 'FAILED']

// «Оқушы жолы»: кім, қашан, не істеді
const Timeline: React.FC<{ intake: CrmIntakeView }> = ({ intake }) => {
    const { formatMessage, locale } = useIntl()
    const subjectOf = (id?: number | null) => subjectTitle(intake.subjects?.find((s) => s.id === id), locale)
    const stepOf = (no?: number | null) => (no ? formatMessage({ id: `crmStep_${no}` }) : '')

    const text = (e: CrmEvent) => {
        const subj = subjectOf(e.subjectId)
        const prefix = subj ? `${subj}: ` : ''
        switch (e.type) {
            case 'CREATED':
                return formatMessage({ id: 'crmEvCreated' }, { name: e.actorName || intake.sellerName || '' })
            case 'EDITED':
                return formatMessage({ id: 'crmEvEdited' })
            case 'APPROVED':
                return formatMessage({ id: 'crmEvApproved' }, { course: courseTitle(intake, locale), date: fmtDate(intake.endDate) })
            case 'ASSIGNED': {
                const track = intake.tracks?.find((t) => t.subject?.id === e.subjectId)
                return (
                    prefix +
                    formatMessage(
                        { id: 'crmEvAssigned' },
                        { group: track?.groupTitle || e.extra || '-', mentor: e.targetUserName || track?.mentorName || '-' },
                    )
                )
            }
            case 'PHOTO':
                return prefix + formatMessage({ id: 'crmEvPhoto' }, { step: stepOf(e.stepNo) })
            case 'VERIFIED':
                return prefix + formatMessage({ id: e.extra === 'false' ? 'crmEvUnverified' : 'crmEvVerified' }, { step: stepOf(e.stepNo) })
            case 'FINISHED':
                return prefix + formatMessage({ id: 'crmEvFinished' })
            case 'NOTIFIED': {
                if (!e.targetUserId) return prefix + formatMessage({ id: 'crmEvNotifiedNobody' })
                const status = NOTIFY_STATUSES.includes(e.extra || '') ? ` (${formatMessage({ id: `crmNotify_${e.extra}` })})` : ''
                return prefix + formatMessage({ id: 'crmEvNotified' }, { name: e.targetUserName || '-' }) + status
            }
            case 'RETURNED':
                return formatMessage({ id: 'crmEvReturned' }, { reason: dictLabel(formatMessage, 'Ret', intake.returnReason) })
            case 'REASSIGNED':
                return formatMessage({ id: 'crmEvReassigned' }, { from: e.fromUserName || '-', to: e.toUserName || '-' })
            case 'DELETED':
                return formatMessage({ id: 'crmEvDeleted' }, { reason: dictLabel(formatMessage, 'Del', e.extra || intake.deleteReason) })
            case 'PAY_REPORTED':
            case 'PAY_CONFIRMED':
            case 'PAY_REJECTED': {
                // extra = «monthNo:amount»
                const [n, sum] = (e.extra || '').split(':')
                return formatMessage({ id: `crmEv_${e.type}` }, { n: n || '-', sum: money(Number(sum) || 0), name: e.actorName || '' })
            }
            case 'ACCESS_SET':
                return formatMessage({ id: 'crmEvAccessSet' }, { date: fmtDate(e.extra) })
            case 'OVERDUE':
                return prefix + formatMessage({ id: 'crmEvOverdue' }, { stage: e.extra ? formatMessage({ id: `crmStage_${e.extra}`, defaultMessage: e.extra }) : '' })
            default:
                return e.type
        }
    }

    const events = [...(intake.timeline ?? [])].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))

    return (
        <Box>
            <Box sx={{ fontSize: '12px', fontWeight: 700, color: ui.muted, letterSpacing: '0.04em', marginBottom: '8px' }}>
                {formatMessage({ id: 'crmStudentPath' })}
            </Box>
            <Box sx={{ borderLeft: `2px solid ${ui.border}`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {events.length === 0 && <Box sx={{ color: ui.muted, fontSize: '13px' }}>-</Box>}
                {events.map((e, i) => (
                    <Box key={i} sx={{ fontSize: '13px', color: ['OVERDUE', 'RETURNED', 'DELETED', 'PAY_REJECTED'].includes(e.type) ? tone.bad.text : ui.ink }}>
                        <b>{whenShort(e.at)}</b> · {text(e)}
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default Timeline
